/**
 * PATH: packages/email-hub/src/feature/components/email-reader/FullReaderWholeHeader.tsx
 * FILE_ID: EMAILHUB-EMAILREADER-WHOLEHEADER-001
 * PURPOSE: Bind the full-reader controller to the shared reader-header adapter.
 * ROLE IN ARCHITECTURE: Host seam parallel to PreviewWholeHeader.
 * MAIN EXPORTS: FullReaderWholeHeader.
 * NON-RESPONSIBILITIES: The shared adapter owns menus, overlays, shortcuts,
 *   and every core-verb mutation (runtime) — this file only routes/navigates.
 * NOTES FOR FUTURE AI: Keep this to binding construction only — full view
 *   must never fork header behavior from preview (plan D269-15).
 */
import { FullReaderHeaderAdapter } from '../../reader-header'
import { resolveReaderPosition } from '../../email-reader/navigation'
import { ZOOM_MAX, ZOOM_MIN } from './useReaderZoom'
import type { EmailReaderController } from './useEmailReaderController'

export function FullReaderWholeHeader({
  controller,
}: {
  controller: EmailReaderController
}) {
  const { email } = controller
  if (!email) return null

  const orderedIds = controller.orderedEmailIds ?? []

  /**
   * Full view does not own the list, so a terminal verb "removes" by
   * LEAVING this email: advance to the next (else previous, else back to
   * the list). The restore closure navigates back to the original email —
   * the list host reconciles row state on its own refetch.
   */
  const beginTerminalRemoval = (): (() => void) | void => {
    const originalId = String(email.id)
    const position =
      orderedIds.length > 0
        ? resolveReaderPosition(orderedIds, originalId)
        : null
    const advanceTo = position?.nextId ?? position?.previousId ?? null
    if (advanceTo) controller.switchEmail(advanceTo)
    else controller.onBack()
    return () => controller.switchEmail(originalId)
  }

  return (
    <FullReaderHeaderAdapter
      email={email}
      narrow={controller.isNarrow}
      backLabel={controller.backLabel}
      onBack={controller.onBack}
      navigation={
        orderedIds.length > 0
          ? {
              orderedIds,
              currentId: String(email.id),
              onNavigate: controller.switchEmail,
            }
          : undefined
      }
      relatedContext={
        controller.relatedContextInHeader
          ? { onOpenEmail: controller.switchEmail }
          : undefined
      }
      onCompose={controller.openCompose}
      onFind={() => controller.find.open()}
      zoom={{
        value: controller.bodyZoom,
        min: ZOOM_MIN,
        max: ZOOM_MAX,
        onZoomIn: controller.zoomIn,
        onZoomOut: controller.zoomOut,
        onReset: controller.resetZoom,
      }}
      pingOpen={controller.pingOpen}
      onPingOpenChange={controller.setPingOpen}
      onLinkJob={
        controller.hideJobLink
          ? undefined
          : () => controller.setShowLinkModal(true)
      }
      terminal={{ beginRemoval: beginTerminalRemoval }}
      kanban={{
        api: controller.kanbanApi,
        orgId: controller.kanbanOrgId,
        onChanged: () => {
          void controller.refetch()
        },
        openAllocatedCard: controller.openAllocatedCard,
        openAllocatedBoard: controller.openAllocatedBoard,
        onPeekJob: controller.setPeekJob,
        canRequestRule: controller.canRequestRule,
        onOpenRuleDialog: () => controller.setRuleDialogOpen(true),
        allocationMembers: controller.allocationMembers,
        hideSenderIdentity: controller.hideSenderIdentity,
      }}
    />
  )
}
